import React, { useEffect, useMemo, useState } from "react";
import useSummary from "../../../useSummary";
import { createColumnHelper } from "@tanstack/react-table";
import "./FindingsByDataSourceAndSeverityTable.scss";
import FindingsBySeverityChart from "../FindingsBySeverity/FindingsBySeverityChart";
import FindingsBySeverityLegend from "../FindingsBySeverity/FindingsSeverityLegend";
import TableChart, {
  type TableChartRowData,
  type TableChartProps,
} from "../TableChart";
import useSetDataWithExport from "../useSetDataWithExport";
import useWidgetFilterNavigator from "../../../useWidgetFilterNavigator";
import useIntegratedDataSources, {
  DatasourceCheckConnectionStatusEnum,
} from "../../../../AppSettings/Integrations/DataSources/useIntegratedDataSources";
import { DateTime } from "luxon";
import useDebounceLoading from "../useDebounceLoading";
import {
  getFormattedDate,
  getRelativeTimeFromNow,
  Tooltip,
  useIcon,
  TableCell,
  tableCellTypesEnum,
  formatNumber,
} from "@SeemplicityDev/shared";
import { integrationCollectionStateEnum } from "@SeemplicityDev/shared/integrations";
import EntityLogo from "../../../../Components/EntityLogo/EntityLogo";
import type {
  WidgetTableProps,
  CountBySeverity,
} from "../TopFeatureTables/TopFeatureTable";

// useSummary is still JS; the parsed `summaryData` for this widget is an array of
// [datasourceName, severityPosition, count] tuples.
type SummaryRow = [string, number, number];

// Per-datasource collection metadata derived from the integrated datasources query.
interface LastCollectionData {
  sourceFilter?: string;
  lastCollectionTime?: string | null;
  hasConnectionError: boolean;
  connectionErrorInstances: string[];
  collectionRunning: boolean;
}

// Minimal shape of an integrated-datasource edge node read by this widget. The
// shared DatasourceEdge.node uses an open index signature, so we narrow locally.
interface IntegratedDataSourceNode {
  datasource_definition?: {
    name?: string;
    source_display_name?: { source_name?: string };
  };
  last_collection_time?: string | null;
  check_connection_status?: string;
  friendly_name?: string;
  collection_state?: string;
}

// Row object rendered by this table. Kept assignable to TableChartRowData via an
// index signature so it can be passed straight to the shared TableChart.
interface FindingsByDataSourceRow extends TableChartRowData {
  source: string;
  logo: string;
  filterFieldValue: string;
  findings: CountBySeverity;
  lastCollectionData?: LastCollectionData;
}

const FindingsByDataSourceAndSeverityTable: React.FC<WidgetTableProps> = ({
  selectedScope,
  scopeToExecute,
  filterToExecute,
  id,
  isForExport = false,
  setTotalWidgets = null,
  index = null,
  setIsReadyToExport = null,
}) => {
  const [tableData, setTableData] = useState<FindingsByDataSourceRow[] | null>(
    null
  );
  const [maxFindings, setMaxFindings] = useState(0);
  const { getDataSourceIcon } = useIcon(isForExport ? "LIGHT" : undefined);
  const {
    summaryData: data,
    summaryDataError,
    summaryLoading,
    refetchSummary,
    // useSummary exposes summaryData as `unknown` at its boundary; this consumer
    // narrows it (and the related fields) to its own row-tuple shape via a cast,
    // matching the other TableCharts widgets. Runtime values are unchanged.
  } = useSummary({
    variables: {
      name: "FINDINGS_VIEW",
      selection: ["finding.id"],
      group_by: ["datasource_definition.name", "finding.severity"],
      aggregate_functions: ["COUNT"],
      round_functions: ["ROUND"],
      filters_config: {
        filtersjson: filterToExecute,
        scopesjson: scopeToExecute || null,
        scopesid: selectedScope?.value?.id,
      },
    },
    skip: (!scopeToExecute && !selectedScope?.value?.id) || !filterToExecute,
  }) as {
    summaryData: SummaryRow[] | null;
    summaryDataError: boolean | null;
    summaryLoading: boolean;
    refetchSummary: (...args: never[]) => unknown;
  };

  const {
    integratedDataSources,
    integratedDataSourcesError,
    loading: integrationsDataLoading,
  } = useIntegratedDataSources(true, true, {}, true);

  const { setDataWithExport, setLoadedRowsCount } =
    useSetDataWithExport<FindingsByDataSourceRow>({
      isForExport,
      index,
      setTotalWidgets,
      setData: setTableData,
      data: tableData,
      error: summaryDataError || integratedDataSourcesError,
      loading: summaryLoading || integrationsDataLoading,
      setIsReadyToExport,
    });

  const isLoading = useDebounceLoading([
    summaryLoading,
    integrationsDataLoading,
  ]);

  const { onFilterClick } = useWidgetFilterNavigator({
    filterToExecute: filterToExecute,
    filterId: null,
    scopeToExecute: scopeToExecute,
    scopeId: selectedScope?.value?.id ?? null,
  });

  const integratedDataSourcesMap = useMemo(() => {
    if (!!integratedDataSources?.datasources) {
      return integratedDataSources?.datasources?.edges?.reduce(
        (
          dsMap: Map<string | undefined, LastCollectionData>,
          currentInstance
        ) => {
          const node = currentInstance.node as
            | IntegratedDataSourceNode
            | undefined;
          const dsName = node?.datasource_definition?.name;
          const currentDS = dsMap.get(dsName);
          const hasConnectionError =
            node?.check_connection_status ===
            DatasourceCheckConnectionStatusEnum.FAILED;
          if (!currentDS) {
            dsMap.set(dsName, {
              sourceFilter:
                node?.datasource_definition?.source_display_name?.source_name,
              lastCollectionTime: node?.last_collection_time,
              hasConnectionError: hasConnectionError,
              connectionErrorInstances:
                hasConnectionError && node?.friendly_name
                  ? [node.friendly_name]
                  : [],
              collectionRunning:
                node?.collection_state ===
                integrationCollectionStateEnum.RUNNING,
            });
          } else {
            dsMap.set(dsName, {
              sourceFilter:
                node?.datasource_definition?.source_display_name?.source_name,
              lastCollectionTime: !currentDS?.lastCollectionTime
                ? node?.last_collection_time
                : DateTime.fromISO(currentDS?.lastCollectionTime ?? "") <
                    DateTime.fromISO(node?.last_collection_time ?? "")
                  ? node?.last_collection_time
                  : currentDS.lastCollectionTime,
              hasConnectionError:
                node?.check_connection_status ===
                DatasourceCheckConnectionStatusEnum.FAILED,
              collectionRunning:
                node?.collection_state ===
                integrationCollectionStateEnum.RUNNING,
              connectionErrorInstances:
                hasConnectionError && node?.friendly_name
                  ? [...currentDS?.connectionErrorInstances, node.friendly_name]
                  : [...currentDS?.connectionErrorInstances],
            });
          }
          return dsMap;
        },
        new Map<string | undefined, LastCollectionData>()
      );
    }
  }, [integratedDataSources]);

  const processData = (data: SummaryRow[]): SummaryRow[] => {
    const result: SummaryRow[] = [];
    const map = new Map<string, number>(); // Map to track combined values for "a" and "b"

    data.forEach(([key, position1, value]) => {
      // Change all "wiz vulnerabilities" to "Wiz"
      if (key.toLowerCase() === "wiz vulnerabilities") {
        key = "Wiz";
      }

      const mapKey = `${key}*${position1}`; // Unique key combining key and position1
      if (map.has(mapKey)) {
        // If already exists, sum the values
        map.set(mapKey, map.get(mapKey)! + value);
      } else {
        // Otherwise, add to map
        map.set(mapKey, value);
      }
    });

    // Convert the map back to the desired array format
    for (const [key, value] of map.entries()) {
      const [newKey, position1] = key.split("*");
      result.push([newKey, parseInt(position1), value]);
    }

    return result;
  };

  useEffect(() => {
    if (!!data && !!integratedDataSourcesMap) {
      /* ------------------------------------------------ */
      /* MERGE WIZ & WIZ VULN - DELETE ASAP               */
      /* ------------------------------------------------ */
      let newData = processData(data);
      /* ------------------------------------------------ */

      const dataArray = newData.reduce(
        (acc: FindingsByDataSourceRow[], curr) => {
          if (!acc?.find((ds) => ds.source === curr[0])) {
            const integratedDS = integratedDataSourcesMap.get(curr[0]);
            const dataObj: FindingsByDataSourceRow = {
              source: curr[0],
              logo: curr[0],
              filterFieldValue: integratedDS?.sourceFilter || curr[0],
              findings: {
                total: curr[2],
              },
              lastCollectionData: integratedDS,
            };
            dataObj.findings[curr[1]] = curr[2];
            acc?.push(dataObj);
          } else {
            const dataObj = acc.find((ds) => ds.source === curr[0])!;
            dataObj.findings[curr[1]] = curr[2];
            dataObj.findings.total += curr[2];
          }

          return acc;
        },
        []
      );

      const max = Math.max(...dataArray?.map((ds) => ds.findings?.total));
      const sortedData = dataArray.sort(
        (a, b) => b.findings.total - a.findings.total
      );

      setMaxFindings(max);
      setDataWithExport(sortedData);
    }
  }, [data, integratedDataSourcesMap]);

  const chartClick = (severity?: string, dataSource?: unknown[]) => {
    onFilterClick([
      ["in", "remediation_item.source", dataSource as string | string[]],
      ["in", "remediation_item.severity", severity as string | string[]],
    ]);
  };

  const columnHelper = createColumnHelper<FindingsByDataSourceRow>();

  const columns = useMemo(() => {
    return [
      columnHelper.accessor("source", {
        cell: (props) => (
          <TableCell>
            <EntityLogo
              name={props.getValue()}
              logo={
                getDataSourceIcon(props.row?.original?.logo, "S") as
                  | string
                  | undefined
              }
              tooltip={props.getValue()}
            />
          </TableCell>
        ),
        enableSorting: false,
        enableColumnFilter: false,
        header: "Source",
        size: 80,
      }),
      columnHelper.accessor("findings", {
        cell: (props) => (
          <TableCell type={tableCellTypesEnum.COMPONENT}>
            <div
              className={`findings-wrapper ${isForExport ? "exported" : ""}`}
            >
              <span
                className={"findings-count"}
                title={formatNumber(props.getValue()?.total) || undefined}
              >
                {formatNumber(props.getValue()?.total)}
              </span>
              <FindingsBySeverityChart
                loading={summaryLoading}
                error={summaryDataError}
                findingCounts={props.getValue()}
                namePrefix={props.row?.original?.logo}
                filterFieldValue={props.row?.original?.filterFieldValue}
                maxFindings={maxFindings}
                widgetId={id}
                onChartClick={chartClick}
                isForExport={isForExport}
                setLoaded={() => setLoadedRowsCount((prev) => prev + 1)}
              />
            </div>
          </TableCell>
        ),
        enableSorting: false,
        enableColumnFilter: false,
        header: "Findings",
        size: 250,
      }),
      columnHelper.accessor("lastCollectionData", {
        cell: (props) => {
          const lastCollectionTime =
            props.getValue()?.lastCollectionTime ?? undefined;
          const displayedDate = props.getValue()?.collectionRunning ? (
            <span className={"no-collection-date"}> Collecting... </span>
          ) : !!lastCollectionTime ? (
            !isForExport ? (
              <span title={getFormattedDate(lastCollectionTime)}>
                {getRelativeTimeFromNow(lastCollectionTime)}
              </span>
            ) : (
              <span>{getFormattedDate(lastCollectionTime)}</span>
            )
          ) : (
            <span className={"no-collection-date"}>N/A</span>
          );

          let errorInstances = (
            props.getValue()
              ? [...(props.getValue()?.connectionErrorInstances ?? [])]
              : []
          )
            ?.splice(0, 3)
            ?.join(",\n");

          if ((props.getValue()?.connectionErrorInstances?.length ?? 0) > 3) {
            errorInstances += "\n...";
          }

          return (
            <>
              <TableCell>
                <div className="last-collection-wrapper">
                  {props.getValue()?.hasConnectionError && (
                    <Tooltip
                      type="info"
                      content={`Connection error: \n${errorInstances}`}
                    >
                      <i className={"seem-icon-error collection-error-icon"} />
                    </Tooltip>
                  )}
                  {displayedDate}
                </div>
              </TableCell>
            </>
          );
        },
        header: () => (
          <>
            <div>
              <div className={"last-collection-header"}>
                Last Collected
                <Tooltip
                  type="info"
                  content={
                    "The most recent collection from integrated instances"
                  }
                >
                  <i className="seem-icon-help collection-details" />
                </Tooltip>
              </div>
            </div>
          </>
        ),
        enableSorting: false,
        enableColumnFilter: false,
        minSize: 220,
      }),
    ];
  }, [
    tableData,
    isForExport,
    maxFindings,
    summaryLoading,
    summaryDataError,
    getDataSourceIcon,
  ]);

  return (
    <TableChart
      className={"findings-by-datasource-and-severity-table"}
      columns={columns as TableChartProps["columns"]}
      tableData={tableData}
      isLoading={isLoading}
      error={summaryDataError || integratedDataSourcesError}
      onRefetch={refetchSummary}
      legend={<FindingsBySeverityLegend />}
      isForExport={isForExport}
    />
  );
};

export default FindingsByDataSourceAndSeverityTable;
